import React, {useMemo, useState} from 'react';
import {Button, Collapse, Divider, Stack, Typography} from "@mui/material";
import {useParams, useSearchParams} from "react-router";
import {removeReferenceMark} from './prosemirror-editor/yMaps/referencesYMap';
import useYDocStore from '../stores/useYDocStore';
import useSearchResultTags from './documentContent/useSearchResultTags';
import SegmentActions from './segmentItem/SegmentActions';
import SegmentContent from './segmentItem/SegmentContent';
import {useDragAndDrop} from './segmentItem/useDragAndDrop';
import {computeLocalId, resolveSegmentIndex, segmentItemPropsAreEqual} from './segmentItem/utils';
import {isProductReportSource} from './segmentItem/sourceCitePolicy';
import CommentsSection from "./comments/CommentsSection";
import {useYCommentsCount} from './comments/useYCommentsCount';
import useSourcePackageBuilderStore from '../stores/useSourcePackageBuilderStore';
import {useSession} from '../firebase/UserProvider';

const EMPTY_TAGS = [];

const SegmentItem = ({props, editorView}) => {
    const {
        docId,
        docUrl,
        docTitle,
        docSource,
        docSourceType,
        docClassificationLevel,
        classificationLevel,
        content,
        // Save/Cite/drag-into-package payload content; falls back to `content` for callers
        // (corpus/data_item/report hits, saved-item re-render) that don't distinguish the two.
        saveContent,
        entities,
        segmentId,
        localId: storedLocalId,
        fileId,
        reportId,
        // Products cite gate: IIR segments remain citable; other report products do not.
        productType,
        documentType,
        // Store-specific tag identifiers (set by unifiedHitToSegmentProps for search hits):
        // Products paragraph id, My Data sentence-segment id, Highlights bookmark item id.
        reportSegmentId,
        dataItemSegmentId,
        highlightDataItemId,
        index,
        offset,
        start, end,
        tags,
        existingTags: suggestedExistingTags,
        newTags: suggestedNewTags,
        segmentType,
        setSegments,
        savedSegmentIndex,
        orderIndex,
        refNumber,
        query,
        provenance,
        score,
        documentDate,
        onContentClick,
        // Source package item actions (passed when segmentType === 'saved')
        itemId,
        folders: spFoldersProp,
        moveItem,
        removeItem,
        displayMode,
        onSelectSegment,
        isSelected,
    } = props;

    // Use docClassificationLevel (search results) or classificationLevel (source package)
    const classification_level = docClassificationLevel || classificationLevel;

    const [searchParams] = useSearchParams();
    const elementName = searchParams.get('elementName');
    const elementId = searchParams.get('elementId');

    // Get active document from zustand store
    const activeDocumentId = useSourcePackageBuilderStore(s => s.documentId);
    const activeSegmentIndex = useSourcePackageBuilderStore(s => s.segmentIndex);
    const openSourcePackageBuilder = useSourcePackageBuilderStore(s => s.open);
    const resolvedLocalId = useMemo(() => (
        computeLocalId({docId, index, offset, start, end}) || storedLocalId || null
    ), [docId, index, offset, start, end, storedLocalId]);
    const resolvedIndex = useMemo(() => (
        resolveSegmentIndex({index, start, localId: resolvedLocalId})
    ), [index, resolvedLocalId, start]);
    const isReference = segmentType === 'reference';
    const isDeltaListMode = displayMode === 'delta-list';
    const isReportProductSource = useMemo(() => isProductReportSource({
        docSourceType,
        reportId,
        localId: resolvedLocalId,
        productType,
    }), [docSourceType, reportId, resolvedLocalId, productType]);
    const isDraggable = !isDeltaListMode && !isReportProductSource;
    const isActive = (Number(activeDocumentId) === docId) && resolvedIndex !== null && (Number(activeSegmentIndex) === resolvedIndex);

    const {reportId: routeReportId} = useParams();

    // What actually gets persisted when this result is saved/cited/dragged into the Source
    // Package — distinct from `content` (the in-context preview shown on the card) for
    // highlight hits, where only the highlighted excerpt should be carried over.
    const contentForSave = saveContent ?? content;

    // Drag and drop handlers
    const {handleDragStart, handleDragEnd} = useDragAndDrop({
        segmentId,
        docId,
        docUrl,
        docTitle,
        docSource,
        docSourceType,
        classificationLevel: classification_level,
        content: contentForSave,
        tags,
        index,
        offset,
        start,
        end,
        isReference,
        orderIndex,
        refNumber,
        itemId,
        localId: resolvedLocalId,
        fileId: fileId ?? null,
        reportId: reportId ?? null,
        productType: productType ?? null,
    });

    function handleRemoveReference() {
        if (!elementId) return;
        const localId = computeLocalId({docId, index, offset, start, end});
        if (!localId) {
            console.error('[segmentItem] Cannot compute localId for reference removal');
            return;
        }
        try {
            const {ydoc, yReferenceMarks} = useYDocStore.getState();
            if (!ydoc || !yReferenceMarks) {
                console.warn('[segmentItem] Y.Doc not ready, cannot remove reference');
                return;
            }
            removeReferenceMark(ydoc, yReferenceMarks, elementId, localId);
            if (editorView && typeof editorView.focus === 'function') {
                editorView.focus();
            }
        } catch (err) {
            console.error('Failed to remove reference:', err);
        }
    }

    function handleOpenSegment() {
        if (isDeltaListMode && typeof onSelectSegment === 'function') {
            onSelectSegment();
            return;
        }
        try {
            openSourcePackageBuilder({
                documentId: docId,
                segmentIndex: resolvedIndex
            });
        } catch (e) {
            console.error('Failed to open segment:', e);
        }
    }

    const isDisabled = !elementName || elementName !== 'report-fragment' || !elementId;
    const stableSegmentTags = useMemo(() => (Array.isArray(tags) ? tags : undefined), [tags]);

    const {tags: segmentTagsLocal, createTag, voteTag, isPosting, loadingTagOps} = useSearchResultTags({
        docSourceType,
        docId,
        segmentId,
        segmentIndex: resolvedIndex,
        segmentTags: stableSegmentTags,
        offset,
        reportSegmentId,
        dataItemSegmentId,
        highlightDataItemId,
    });

    const localId = resolvedLocalId;
    const {count: commentCount} = useYCommentsCount({elementName: 'segment', elementId: localId});
    const [showComments, setShowComments] = useState(false);

    const session = useSession();
    const isAdmin = Boolean(
        session?.user?.isAdmin
        || session?.role === 'admin'
        || (session?.roles || []).includes('admin')
    );
    const showScore = isAdmin && segmentType === 'search' && typeof score === 'number' && Number.isFinite(score);
    const scorePercentLabel = showScore ? `Relevance ${Math.round(Math.max(0, Math.min(1, score)) * 100)}%` : null;

    const isCardActive = isDeltaListMode ? Boolean(isSelected) : isActive;
    const actionIdentifiers = useMemo(() => ({
        docId,
        // Stable identity for Save/Cite — explicit localId (set by federated search mapping
        // for non-corpus hits) takes precedence so every result saves as a distinct item.
        localId: resolvedLocalId,
        docUrl,
        docTitle,
        docSource,
        docSourceType,
        classificationLevel: classification_level,
        content: contentForSave,
        segmentId,
        // Non-corpus source identifiers — null for corpus hits.
        // fileId: My Data file reference; reportId: Reports result.
        // Forwarded into the Yjs reference entry so the margin-rail viewer can open
        // the correct document without the extractIdFromLocalId fallback.
        fileId: fileId ?? null,
        reportId: reportId ?? null,
        productType: productType ?? null,
        documentType: documentType ?? null,
        index,
        offset,
        start, end,
        tags: Array.isArray(stableSegmentTags) ? stableSegmentTags : EMPTY_TAGS,
    }), [
        docId,
        resolvedLocalId,
        docUrl,
        docTitle,
        docSource,
        docSourceType,
        classification_level,
        contentForSave,
        segmentId,
        fileId,
        reportId,
        productType,
        documentType,
        index,
        offset,
        start, end,
        stableSegmentTags
    ]);

    return (
        <Stack
            bgcolor={isReference ? null : (isCardActive ? '#eeeeff' : 'white')}
            borderRadius={isReference ? 0 : 2}
            outline={isReference ? null : (isCardActive ? '2px dotted blue' : '1px solid #cde')}
            overflow={'hidden'}
            flexShrink={0}
            draggable={isDraggable}
            onDragStart={isDraggable ? handleDragStart : undefined}
            onDragEnd={isDraggable ? handleDragEnd : undefined}
            sx={{
                cursor: isDeltaListMode ? 'pointer' : (isDraggable ? 'grab' : 'default'),
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                    zIndex: 100,
                    boxShadow: 5,
                }
            }}>
            <Stack
                p={2}
                spacing={1}
                onClick={typeof onContentClick === 'function' ? () => onContentClick() : undefined}
            >
                {!isDeltaListMode && (
                    <SegmentActions
                        segmentType={segmentType}
                        reportId={reportId}
                        orderIndex={orderIndex}
                        savedSegmentIndex={savedSegmentIndex}
                        setSegments={setSegments}
                        itemId={itemId}
                        folders={spFoldersProp}
                        moveItem={moveItem}
                        removeItem={removeItem}
                        identifiers={actionIdentifiers}
                    />
                )}

                <SegmentContent
                    content={content}
                    entities={entities}
                    docTitle={docTitle}
                    docSource={docSource}
                    docSourceType={docSourceType}
                    classificationLevel={classification_level}
                    segmentType={segmentType}
                    query={query}
                    provenance={provenance}
                    documentDate={documentDate}
                    productType={productType}
                    isReference={isReference}
                    refNumber={refNumber}
                    isDisabled={isDisabled}
                    handleRemoveReference={handleRemoveReference}
                    handleOpenSegment={handleOpenSegment}
                    openHandledByParent={typeof onContentClick === 'function'}
                    segmentTagsLocal={segmentTagsLocal}
                    createTag={createTag}
                    voteTag={voteTag}
                    isPosting={isPosting}
                    loadingTagOps={loadingTagOps}
                    docId={docId}
                    suggestedExistingTags={suggestedExistingTags}
                    suggestedNewTags={suggestedNewTags}
                    segmentId={segmentId}
                    dataItemSegmentId={dataItemSegmentId}
                    index={index}
                    offset={offset}
                    start={start}
                    end={end}
                    // compact={isDeltaListMode}
                    compact={false}
                />
            </Stack>

            {!isDeltaListMode && (
                <>
                    <Divider/>

                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Button
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowComments(!showComments);
                            }}
                            sx={{
                                textTransform: 'none',
                                justifyContent: 'flex-start',
                                px: 2,
                                py: 1,
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'action.hover'
                                }
                            }}
                        >
                            {showComments ? 'Hide comments' : `Comments (${commentCount})`}
                        </Button>

                        {showScore && (
                            <Typography
                                component="span"
                                title="Relevance score — normalized search ranking (admin only)"
                                sx={{
                                    mr: 2,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    lineHeight: 1.4,
                                    color: 'primary.main',
                                    letterSpacing: 0.2,
                                    whiteSpace: 'nowrap',
                                    userSelect: 'none',
                                }}
                            >
                                {scorePercentLabel}
                            </Typography>
                        )}
                    </Stack>

                    <Collapse in={showComments}>
                        <CommentsSection props={{
                            elementName: 'segment',
                            elementId: localId,
                            autoFocus: showComments
                        }}/>
                    </Collapse>
                </>
            )}
        </Stack>
    );
};


export default React.memo(SegmentItem, segmentItemPropsAreEqual);
